import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { cn } from "@/lib/utils";
import { Bold, Italic, Underline, List, ListOrdered, RotateCcw, Link2 } from "lucide-react";
import { Label } from "./label";
import { Button } from "./button";

type RichTextEditorProps = Omit<React.ComponentProps<"div">, "onChange"> & {
	label?: string;
	value?: string;
	onChange?: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	onFocus?: () => void;
	onDropValue?: (value: string) => void;
};

export type RichTextEditorHandle = {
	insertText: (text: string) => void;
};

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(({
	className,
	label,
	value = "",
	onChange,
	placeholder = "Enter text...",
	disabled = false,
	onFocus,
	onDropValue,
	...props
}, ref) => {
	const editorRef = useRef<HTMLDivElement>(null);
	const selectionRef = useRef<Range | null>(null);
	const [isFocused, setIsFocused] = useState(false);
	const [showLinkPopover, setShowLinkPopover] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [linkText, setLinkText] = useState("");

	useEffect(() => {
		// While the link popover is open the editor is blurred (its URL input has
		// focus). Don't rewrite innerHTML or drop the saved selection here, or the
		// caret/highlight captured when the popover opened would be lost.
		if (editorRef.current && !isFocused && !showLinkPopover) {
			editorRef.current.innerHTML = value || "";
			selectionRef.current = null;
		}
	}, [value, isFocused, showLinkPopover]);

	const execCommand = (command: string, showUI?: boolean, value?: string) => {
		// Ensure editor has focus
		editorRef.current?.focus();

		// For list commands, ensure we're creating proper list structure
		if (command === "insertUnorderedList" || command === "insertOrderedList") {
			// Place cursor at end if there's content
			if (editorRef.current && editorRef.current.innerHTML.trim()) {
				const range = document.createRange();
				const sel = window.getSelection();
				range.selectNodeContents(editorRef.current);
				range.collapse(false);
				sel?.removeAllRanges();
				sel?.addRange(range);
			}
		}

		document.execCommand(command, showUI || false, value);
		updateContent();

		// Refocus after command execution
		setTimeout(() => {
			editorRef.current?.focus();
		}, 0);
	};

	const updateContent = () => {
		if (editorRef.current && onChange) {
			onChange(editorRef.current.innerHTML);
		}
	};

	const saveSelection = () => {
		const editor = editorRef.current;
		const selection = window.getSelection();
		if (!editor || !selection || selection.rangeCount === 0) return;

		const range = selection.getRangeAt(0);
		if (!editor.contains(range.commonAncestorContainer)) return;
		selectionRef.current = range.cloneRange();
	};

	const restoreSelection = () => {
		const editor = editorRef.current;
		const selection = window.getSelection();
		if (!editor || !selection) return false;

		if (selectionRef.current) {
			selection.removeAllRanges();
			selection.addRange(selectionRef.current);
			return true;
		}

		if (!editor.innerHTML.trim()) {
			return false;
		}

		const range = document.createRange();
		range.selectNodeContents(editor);
		range.collapse(false);
		selection.removeAllRanges();
		selection.addRange(range);
		selectionRef.current = range.cloneRange();
		return true;
	};

	const insertText = (text: string) => {
		editorRef.current?.focus();
		restoreSelection();
		document.execCommand("insertText", false, text);
		saveSelection();
		updateContent();
	};

	useImperativeHandle(ref, () => ({
		insertText,
	}));

	const escapeHtml = (text: string) =>
		text
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;");

	// Add a protocol when the user omits one so the href resolves correctly.
	const normalizeUrl = (url: string) => {
		const trimmed = url.trim();
		if (!trimmed) return "";
		if (/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(trimmed)) return trimmed;
		return `https://${trimmed}`;
	};

	const openLinkPopover = () => {
		saveSelection();
		const selectedText = window.getSelection()?.toString() ?? "";
		setLinkText(selectedText);
		setLinkUrl("");
		setShowLinkPopover(true);
	};

	const closeLinkPopover = () => {
		setShowLinkPopover(false);
		setLinkUrl("");
		setLinkText("");
	};

	const applyLink = () => {
		const href = normalizeUrl(linkUrl);
		if (!href) return;

		const editor = editorRef.current;
		if (!editor) return;

		// Capture the selection saved when the popover opened *before* focusing
		// the editor — focusing fires onFocus → saveSelection, which would
		// otherwise overwrite selectionRef with a caret at the start.
		const savedRange = selectionRef.current;

		editor.focus();

		const selection = window.getSelection();
		if (selection) {
			selection.removeAllRanges();
			if (savedRange) {
				selection.addRange(savedRange);
			} else {
				// No saved selection — fall back to the end of the content.
				const range = document.createRange();
				range.selectNodeContents(editor);
				range.collapse(false);
				selection.addRange(range);
			}
		}

		const display = linkText.trim() || href;
		// insertHTML replaces the current selection (if any) with the anchor,
		// covering both "link the selected text" and "insert a new link".
		document.execCommand(
			"insertHTML",
			false,
			`<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(display)}</a>`
		);

		saveSelection();
		updateContent();
		closeLinkPopover();
	};

	const handleInput = () => {
		saveSelection();
		updateContent();
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text/plain");
		restoreSelection();
		document.execCommand("insertText", false, text);
		saveSelection();
		updateContent();
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		const text = e.dataTransfer.getData("text/plain");
		if (!text) return;
		onDropValue?.(text);
		insertText(text);
	};

	return (
		<div {...props}>
			{!!label && (
				<div className="my-3 capitalize">
					<Label>{label}</Label>
				</div>
			)}
			<div className="border border-gray-500 dark:border-gray-700 rounded-md overflow-hidden shadow-sm">
				{/* Toolbar */}
				<div className="flex flex-wrap gap-1 bg-gray-50 dark:bg-neutral-800 p-2 border-b border-gray-200">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onMouseDown={(e) => {
							e.preventDefault();
							execCommand("bold");
						}}
						className="h-8 w-8 p-0 hover:bg-gray-200"
						disabled={disabled}
						title="Bold (Ctrl+B)"
					>
						<Bold className="w-4 h-4" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onMouseDown={(e) => {
							e.preventDefault();
							execCommand("italic");
						}}
						className="h-8 w-8 p-0 hover:bg-gray-200"
						disabled={disabled}
						title="Italic (Ctrl+I)"
					>
						<Italic className="w-4 h-4" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onMouseDown={(e) => {
							e.preventDefault();
							execCommand("underline");
						}}
						className="h-8 w-8 p-0 hover:bg-gray-200"
						disabled={disabled}
						title="Underline (Ctrl+U)"
					>
						<Underline className="w-4 h-4" />
					</Button>
					<div className="w-px bg-gray-300" />
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onMouseDown={(e) => {
							e.preventDefault();
							execCommand("insertUnorderedList");
						}}
						className="h-8 w-8 p-0 hover:bg-gray-200"
						disabled={disabled}
						title="Bullet List"
					>
						<List className="w-4 h-4" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onMouseDown={(e) => {
							e.preventDefault();
							execCommand("insertOrderedList");
						}}
						className="h-8 w-8 p-0 hover:bg-gray-200"
						disabled={disabled}
						title="Numbered List"
					>
						<ListOrdered className="w-4 h-4" />
					</Button>
					<div className="w-px bg-gray-300" />
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onMouseDown={(e) => {
							e.preventDefault();
							execCommand("removeFormat");
						}}
						className="h-8 w-8 p-0 hover:bg-gray-200"
						disabled={disabled}
						title="Clear Formatting"
					>
						<RotateCcw className="w-4 h-4" />
					</Button>
					<div className="w-px bg-gray-300" />
					<div className="relative">
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onMouseDown={(e) => {
								e.preventDefault();
								if (showLinkPopover) {
									closeLinkPopover();
								} else {
									openLinkPopover();
								}
							}}
							className="h-8 w-8 p-0 hover:bg-gray-200"
							disabled={disabled}
							title="Insert Link"
						>
							<Link2 className="w-4 h-4" />
						</Button>
						{showLinkPopover && (
							<div
								className="absolute left-0 top-10 z-20 w-72 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-900 p-3 shadow-lg"
								onMouseDown={(e) => e.stopPropagation()}
							>
								<div className="space-y-2 text-left">
									<input
										type="text"
										value={linkText}
										onChange={(e) => setLinkText(e.target.value)}
										placeholder="Text to display"
										className="w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm outline-none focus:border-gray-500"
									/>
									<input
										type="url"
										value={linkUrl}
										onChange={(e) => setLinkUrl(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												applyLink();
											}
										}}
										placeholder="https://example.com"
										autoFocus
										className="w-full rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1 text-sm outline-none focus:border-gray-500"
									/>
									<div className="flex justify-end gap-2 pt-1">
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="h-7 px-2 text-xs"
											onMouseDown={(e) => {
												e.preventDefault();
												closeLinkPopover();
											}}
										>
											Cancel
										</Button>
										<Button
											type="button"
											size="sm"
											className="h-7 px-2 text-xs"
											disabled={!linkUrl.trim()}
											onMouseDown={(e) => {
												e.preventDefault();
												applyLink();
											}}
										>
											Add Link
										</Button>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Editor Container with max-height and scroll */}
				<div className="max-h-96 overflow-y-auto bg-white dark:bg-neutral-950">
					{/* Editor */}
					<div
						ref={editorRef}
						contentEditable={!disabled}
						suppressContentEditableWarning
						onInput={handleInput}
						onFocus={() => {
							setIsFocused(true);
							saveSelection();
							onFocus?.();
						}}
						onBlur={() => {
							// Don't re-capture the selection when focus moves to the link
							// popover's input — that would clobber the range saved on open
							// (and can collapse a highlight before it becomes a link).
							if (!showLinkPopover) {
								saveSelection();
							}
							setIsFocused(false);
							updateContent();
						}}
						onKeyUp={saveSelection}
						onMouseUp={saveSelection}
						onPaste={handlePaste}
						onDrop={handleDrop}
						onDragOver={(e) => e.preventDefault()}
						className={cn(
							"min-h-64 w-full px-4 py-3 text-base outline-none bg-transparent",
							"text-gray-700 dark:text-gray-300",
							"focus:ring-0",
							"empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400",
							"[&_ul]:list-disc [&_ul]:ml-6",
							"[&_ol]:list-decimal [&_ol]:ml-6",
							"[&_li]:mb-1",
							"[&_a]:text-blue-600 [&_a]:underline",
							disabled && "opacity-50 cursor-not-allowed",
							className
						)}
						data-placeholder={placeholder}
					/>
				</div>
			</div>
		</div>
	);
});

RichTextEditor.displayName = "RichTextEditor";
